// UserHashtagCard.jsx
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import WithIntersectionObserver from '../component/WithIntersectionObserver.jsx';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import LiveStreamCard from '../container/LiveStreamCard.js';

import media from '../style/media.js';
import { textColor, color } from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';

export const UserHashtagCard = ({
  userId = '',
  displayName = '',
  username = '',
  category = '',
  hashtags = EMPTY_ARRAY,
  hashtagFeedUser = '',
  streamTitle = '',
  privateShowTitle = '',
  shouldShowUserHashtags = false,
  index = null,
  rank = null,
  shouldShowRank = false,
  isStreaming = false,
  isExclusive = false,
}) => {
  const { t } = useTranslation();
  const hasTitle = !!privateShowTitle || !!streamTitle || isExclusive;
  return (
    <StyledUserHashtagDetail>
      <WithIntersectionObserver key={userId} shouldKeepObserve>
        {({ isIntersecting }) => (
          <LiveStreamCardWrapper>
            <LiveStreamCard
              userId={userId}
              streamId={userId}
              isIntersecting={isIntersecting}
              isOnUserGrid={true}
              isDisplayRankEnabled={shouldShowRank}
              rank={rank}
              category={category}
              index={index}
              categoryId={category}
              id={userId}
              itemIndexInCategory={index}
            />
            <MetaData>
              <StreamTitle>
                {isExclusive
                  ? t('exclusive_stream_title', {
                      ns: TranslationNamespace.LIVESTREAM,
                    })
                  : privateShowTitle || streamTitle}
              </StreamTitle>
              <UsernameWithRank
                to={`/user/${userId}`}
                data-element_id={ButtonId.All.ButtonUsername}
                data-tracking_payload={{
                  'discover.index': index,
                  'discover.category': category,
                }}
              >
                <UsernameWrapper>
                  <DisplayName
                    to={`/user/${userId}`}
                    data-element_id={ButtonId.All.UserSquareCardName}
                    data-tracking_payload={{
                      'user.id': userId,
                      'user.displayName': displayName,
                      'user.username': username,
                      'discover.index': index,
                      'discover.category': category,
                    }}
                    $hasTitle={hasTitle}
                  >
                    {displayName || `@${username}`}
                  </DisplayName>
                  {!isStreaming && (
                    <UserStatusIndicatorWrapper>
                      <UserStatusIndicator
                        userId={userId}
                        livestreamSize={12}
                        shouldShowLiveStream
                        shouldShowOnline
                      />
                    </UserStatusIndicatorWrapper>
                  )}
                </UsernameWrapper>
                {displayName && !hasTitle && (
                  <Username>
                    <Link
                      to={`/user/${userId}`}
                      data-element_id={ButtonId.All.UserSquareCardName}
                      data-tracking_payload={{
                        'user.id': userId,
                        'user.username': username,
                        'discover.index': index,
                        'discover.category': category,
                      }}
                    >
                      @{username}
                    </Link>
                  </Username>
                )}
              </UsernameWithRank>
              {shouldShowUserHashtags && (
                <Hashtags>
                  {hashtags.slice(0, 2).map((tag, index) => {
                    const feedName = hashtagFeedUser.replace('{HASHTAG}', tag);
                    return (
                      <HashtagLink
                        key={index}
                        to={`/user-feed-grid/${feedName}`}
                        data-element_id={ButtonId.Hashtag.ButtonUserHashtag}
                        data-tracking_payload={{
                          hashtag: tag,
                          'discover.index': index,
                          'discover.category': category,
                        }}
                      >
                        {t(`user_hashtag_${tag}`, {
                          ns: TranslationNamespace.FEED,
                        })}
                      </HashtagLink>
                    );
                  })}
                </Hashtags>
              )}
            </MetaData>
          </LiveStreamCardWrapper>
        )}
      </WithIntersectionObserver>
    </StyledUserHashtagDetail>
  );
};

UserHashtagCard.propTypes = {
  userId: PropTypes.string,
  displayName: PropTypes.string,
  username: PropTypes.string,
  category: PropTypes.string,
  hashtagFeedUser: PropTypes.string,
  streamTitle: PropTypes.string,
  privateShowTitle: PropTypes.string,
  rank: PropTypes.number,
  index: PropTypes.number,
  hashtags: PropTypes.array,
  shouldShowUserHashtags: PropTypes.bool,
  shouldShowRank: PropTypes.bool,
  isStreaming: PropTypes.bool,
  isExclusive: PropTypes.bool,
};

const StyledUserHashtagDetail = styled.div`
  width: 100%;
  height: 100%;
`;

const LiveStreamCardWrapper = styled.article``;

const MetaData = styled.div`
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
  padding: 4px 8px;
  background-color: ${color.neutral[10]};
  display: flex;
  flex-direction: column;
  min-height: 56px;

  ${media.tablet`
    min-height: 44px;
  `}
`;

const UsernameWithRank = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-template-rows: auto auto;
  grid-template-areas:
    'display-name display-name'
    'username username';
  grid-column-gap: 4px;
  align-items: baseline;
  overflow: hidden;
`;

const UsernameWrapper = styled.div`
  overflow: hidden;
  grid-area: display-name;
  display: flex;
  align-items: center;
  flex: auto;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${color.neutral[100]};
  ${media.tablet`
    font-size: 12px;
  `}
`;

const DisplayName = styled(Link)`
  ${TextEllipsis};
  color: ${({ $hasTitle }) =>
    $hasTitle ? color.neutral[75] : color.neutral[100]};
`;

const Username = styled.div`
  ${TextEllipsis};
  grid-area: username;
  overflow: hidden;
  color: ${color.neutral[75]};
  font-size: 14px;
  line-height: 150%;
  ${media.tablet`
    font-size: 12px;
  `}
`;

const UserStatusIndicatorWrapper = styled.span`
  margin-left: 4px;
  height: 24px; // should be as same as UsernameWrapper 16px * 1.5
  flex: none;
  display: inline-flex;
  align-items: center;
  ${media.tablet`
    height: 18px; // should be as same as UsernameWrapper 12px * 1.5
  `};
`;

const Hashtags = styled.div`
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  display: -webkit-box;
  margin-top: 4px;
`;

const HashtagLink = styled(Link)`
  margin-right: 4px;
  display: inline-block;
  border-radius: 40px;
  padding: 2px 8px;
  background-color: rgba(221, 221, 221, 0.2);
  color: ${textColor.primary};
  line-height: 150%; /* 24px */
  letter-spacing: 0.024px;
  font-size: 14px;
  ${media.mobile`
    font-size: 12px;
  `};
`;

const StreamTitle = styled.div`
  ${TextEllipsis}
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 18px */
  ${media.tablet` 
    font-size: 12px;
  `}
`;

export default UserHashtagCard;
